import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { localStorageKeys } from '@/utils/tokens';
import { saveFilterToLocalStorage } from '@/utils/localStorage';
import { SortDirection } from '@/utils/sortingUtils';

const ALLOWED_FILTERS = ['sortOptions'];

export type WorkItemSortField =
  | 'type'
  | 'contractId'
  | 'route.depotName'
  | 'route.blockExternalId'
  | 'route.lineNumber'
  | 'schedule.departure'
  | 'schedule.arrival'
  | 'route.startingStopName'
  | 'delayInfo.delaySeconds'
  | 'ptaReportId';

export interface SortOption {
  field: WorkItemSortField;
  direction: SortDirection;
}

export interface WorkItemSortingState {
  sortOptions: SortOption[];
}

export const defaultWorkItemSorting: WorkItemSortingState = {
  sortOptions: [],
};

const initialState: WorkItemSortingState = {
  ...defaultWorkItemSorting,
  ...JSON.parse(localStorage.getItem(localStorageKeys.workItemSorting) || '{}'),
};

const workItemSortingSlice = createSlice({
  name: 'workItemSorting',
  initialState,
  reducers: {
    setSortOptions(
      state,
      action: PayloadAction<{
        field: WorkItemSortField;
        direction?: SortDirection;
      }>
    ) {
      const { field, direction } = action.payload;

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );

      let newDirection: SortDirection;
      if (
        existingIndex !== -1 &&
        !direction &&
        state.sortOptions[existingIndex]
      ) {
        newDirection =
          state.sortOptions[existingIndex].direction === 'asc' ? 'desc' : 'asc';
      } else {
        newDirection = direction || 'asc';
      }

      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);
      }

      state.sortOptions.push({ field, direction: newDirection });

      saveFilterToLocalStorage(
        localStorageKeys.workItemSorting,
        state,
        ALLOWED_FILTERS
      );
    },
    removeSortField(
      state,
      action: PayloadAction<{ field: WorkItemSortField }>
    ) {
      const { field } = action.payload;

      const existingIndex = state.sortOptions.findIndex(
        (opt) => opt.field === field
      );

      if (existingIndex !== -1) {
        state.sortOptions.splice(existingIndex, 1);

        saveFilterToLocalStorage(
          localStorageKeys.workItemSorting,
          state,
          ALLOWED_FILTERS
        );
      }
    },
    resetSortOptions(state) {
      state.sortOptions = [...defaultWorkItemSorting.sortOptions];
      saveFilterToLocalStorage(
        localStorageKeys.workItemSorting,
        state,
        ALLOWED_FILTERS
      );
    },
  },
});

export const { setSortOptions, removeSortField, resetSortOptions } =
  workItemSortingSlice.actions;

export default workItemSortingSlice.reducer;
